/**
 * Homly — WelcomeChecklist
 * ========================
 * Checklist d'onboarding pour les nouvelles agences. Affichée au-dessus du
 * cockpit Manager tant que toutes les étapes ne sont pas complétées (ou
 * jusqu'à ce que l'utilisateur la masque volontairement via "Plus tard").
 *
 * Phase 4 de la refonte UX "Dark Luxury".
 *
 * Props :
 *   - stats: { active_mandates, total_mandates }
 *   - team:  Array<{...}>
 *   - basePath: '/admin' ou '/agent'
 *   - onDismiss: () => void (optional)
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, PenTool, Users, Settings, ArrowRight, X, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'homly_welcome_checklist_hidden';

export default function WelcomeChecklist({ stats = {}, team = [], basePath = '/admin', onDismiss }) {
    const navigate = useNavigate();
    const [hidden, setHidden] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEY) === '1'; }
        catch { return false; }
    });

    // Steps definition
    const steps = [
        {
            id: 'mandate',
            label: 'Créer ou uploader votre premier mandat',
            description: 'Saisie manuelle, upload de PDF, ou photo de mandat papier (avec validation IA).',
            done: (stats.total_mandates || 0) > 0,
            cta: { label: 'Uploader un mandat', icon: PenTool, to: `${basePath}/mandates/upload` },
        },
        {
            id: 'team',
            label: 'Inviter votre équipe',
            description: 'Ajoutez vos agents pour qu\'ils accèdent à leur propre cockpit.',
            done: (team?.length || 0) > 1,
            cta: { label: 'Ajouter un agent', icon: Users, to: `${basePath}/team` },
            onlyAdmin: true,
        },
        {
            id: 'settings',
            label: 'Personnaliser votre agence',
            description: 'Logo, coordonnées, taux de commission par défaut.',
            done: false, // Toujours considéré non-fait — l'utilisateur peut le marquer en fermant
            cta: { label: 'Paramètres agence', icon: Settings, to: `${basePath}/settings` },
            onlyAdmin: true,
        },
    ];

    const isAdmin = basePath === '/admin';
    const visibleSteps = steps.filter((s) => !s.onlyAdmin || isAdmin);
    const completedCount = visibleSteps.filter((s) => s.done).length;
    const allDone = completedCount === visibleSteps.length;

    // Hide if user has dismissed OR if all done
    if (hidden || allDone) return null;

    const handleDismiss = () => {
        try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
        setHidden(true);
        onDismiss?.();
    };

    const progressPct = (completedCount / visibleSteps.length) * 100;

    return (
        <div
            data-testid="welcome-checklist"
            style={{
                position: 'relative',
                background: 'linear-gradient(135deg, rgba(104, 23, 147, 0.10) 0%, rgba(255, 138, 61, 0.06) 100%), var(--homly-bg-elevated)',
                border: '1px solid var(--homly-purple-border)',
                borderRadius: 'var(--homly-radius-xl)',
                padding: 'var(--homly-space-6)',
                marginBottom: 'var(--homly-space-6)',
                boxShadow: 'var(--homly-shadow-glow-purple)',
                overflow: 'hidden',
                fontFamily: 'var(--homly-font-body)',
            }}
        >
            {/* Halo décoratif */}
            <span
                aria-hidden="true"
                style={{
                    position: 'absolute',
                    top: -60, right: -60,
                    width: 200, height: 200,
                    background: 'radial-gradient(circle, rgba(168, 85, 247, 0.25), transparent 60%)',
                    pointerEvents: 'none',
                }}
            />

            {/* Close button */}
            <button
                type="button"
                onClick={handleDismiss}
                data-testid="welcome-checklist-dismiss"
                aria-label="Masquer la checklist"
                style={{
                    position: 'absolute',
                    top: 12, right: 12,
                    width: 28, height: 28,
                    background: 'transparent',
                    border: '1px solid var(--homly-border)',
                    borderRadius: 'var(--homly-radius-sm)',
                    color: 'var(--homly-text-tertiary)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all var(--homly-transition-fast)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--homly-text-primary)'; e.currentTarget.style.background = 'var(--homly-bg-card-2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--homly-text-tertiary)'; e.currentTarget.style.background = 'transparent'; }}
            >
                <X size={14} />
            </button>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <div
                    style={{
                        width: 36, height: 36,
                        borderRadius: 'var(--homly-radius-md)',
                        background: 'var(--homly-gradient-purple)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(168, 85, 247, 0.35)',
                    }}
                >
                    <Sparkles size={18} strokeWidth={2.2} />
                </div>
                <div>
                    <p style={{ margin: 0, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--homly-orange)', fontFamily: 'var(--homly-font-display)', fontWeight: 700 }}>
                        Bienvenue sur Homly
                    </p>
                    <h2 style={{ margin: '2px 0 0 0', fontSize: 18, fontWeight: 700, color: 'var(--homly-text-primary)', fontFamily: 'var(--homly-font-display)' }}>
                        Configurez votre cockpit en {visibleSteps.length} étape{visibleSteps.length > 1 ? 's' : ''}
                    </h2>
                </div>
            </div>

            {/* Progress bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
                <div style={{ flex: 1, height: 6, background: 'var(--homly-bg-card-2)', borderRadius: 'var(--homly-radius-pill)', overflow: 'hidden' }}>
                    <div
                        style={{
                            height: '100%',
                            width: `${progressPct}%`,
                            background: 'var(--homly-gradient-orange)',
                            borderRadius: 'var(--homly-radius-pill)',
                            transition: 'width 500ms cubic-bezier(0.22, 1, 0.36, 1)',
                        }}
                    />
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--homly-orange)', fontFamily: 'var(--homly-font-mono)', whiteSpace: 'nowrap' }}>
                    {completedCount}/{visibleSteps.length}
                </span>
            </div>

            {/* Steps */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', zIndex: 1 }}>
                {visibleSteps.map((step) => {
                    const CTAIcon = step.cta.icon;
                    return (
                        <div
                            key={step.id}
                            data-testid={`welcome-step-${step.id}`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                padding: '12px 14px',
                                background: step.done ? 'rgba(34, 211, 162, 0.08)' : 'var(--homly-bg-card-2)',
                                border: step.done ? '1px solid rgba(34, 211, 162, 0.20)' : '1px solid var(--homly-border)',
                                borderRadius: 'var(--homly-radius-md)',
                            }}
                        >
                            {/* Status icon */}
                            {step.done ? (
                                <CheckCircle2 size={20} style={{ color: 'var(--homly-priority-ok)', flexShrink: 0 }} />
                            ) : (
                                <Circle size={20} style={{ color: 'var(--homly-text-tertiary)', flexShrink: 0 }} />
                            )}

                            {/* Step info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                    style={{
                                        fontSize: 13.5,
                                        fontWeight: 600,
                                        color: step.done ? 'var(--homly-text-secondary)' : 'var(--homly-text-primary)',
                                        textDecoration: step.done ? 'line-through' : 'none',
                                        fontFamily: 'var(--homly-font-display)',
                                    }}
                                >
                                    {step.label}
                                </div>
                                {!step.done && (
                                    <div style={{ fontSize: 12, color: 'var(--homly-text-tertiary)', marginTop: 2 }}>
                                        {step.description}
                                    </div>
                                )}
                            </div>

                            {/* CTA */}
                            {!step.done && (
                                <button
                                    type="button"
                                    onClick={() => navigate(step.cta.to)}
                                    data-testid={`welcome-step-${step.id}-cta`}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '7px 14px',
                                        background: 'var(--homly-gradient-purple)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: 'var(--homly-radius-pill)',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        fontFamily: 'var(--homly-font-display)',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        boxShadow: '0 2px 8px rgba(168, 85, 247, 0.20)',
                                        transition: 'transform var(--homly-transition-fast)',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                                >
                                    <CTAIcon size={13} />
                                    <span>{step.cta.label}</span>
                                    <ArrowRight size={12} />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
